/**
 * @typedef NotesCreatorState
 * @property {boolean} savable - true if the notes creator is in a savable state
 * @property {Note[]} notes - array of new note candidates
 */

import { maxNoteLength } from 'shared/components/data-display/helper';

/**
 * @param {Note[]} notes - array of new note candidates
 * @returns {boolean} - true if the new note inputs are in a saveable state
 */
const isSavable = (notes) => {
  if (notes.length === 0) return false;

  if (notes.some((note) => !note.value)) return false;

  if (notes.some((note) => !note.note_type?.name)) return false;

  return !notes.some((note) => note.value?.length > maxNoteLength);
};

const newNote = (entityType, entityID) => ({
  id: `temp-${new Date().getTime()}`,
  entity_type: entityType,
  entity_id: entityID,
  note_type: null,
});

/**
 * @return NotesCreatorState
 */
export const getInitialState = (entityType, entityID) => ({
  savable: false,
  dirty: false,
  notes: [newNote(entityType, entityID)],
});

/**
 *
 * @param {NotesCreatorState} state
 * @param {Object} action
 * @returns {NotesCreatorState}
 */
const notesCreatorReducer = (state, action) => {
  switch (action.type) {
    case 'added': {
      return {
        savable: false,
        notes: [...state.notes, newNote(action.entityType, action.entityID)],
      };
    }
    case 'removed': {
      const notes = state.notes.filter((note, i) => i !== action.index);
      if (notes.length === 0) {
        notes.push(newNote(action.entityType, action.entityID));
      }
      return {
        savable: isSavable(notes),
        notes,
      };
    }
    case 'changeLabel': {
      const notes = [...state.notes];
      notes[action.index].note_type = action.noteType ? { ...action.noteType } : null;
      return {
        savable: isSavable(notes),
        notes,
      };
    }
    case 'changeValue': {
      const notes = [...state.notes];
      notes[action.index].value = action.value;

      return {
        savable: isSavable(notes),
        notes,
      };
    }
    case 'saved': {
      return { savable: false, notes: [newNote(action.entityType, action.entityID)] };
    }
    default: {
      throw new Error(`unknown action ${action.type}`);
    }
  }
};

export default notesCreatorReducer;
